import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { throwIfNotOk } from "@/lib/api-error";
import { validateSettingsPatch } from "@/lib/platform-settings-patch";
import {
  PLATFORM_CLOSED_KEY,
  PLATFORM_LOCK_PASSWORD_INPUT_KEY,
} from "@/lib/platform-lock-shared";
import {
  FloppyDisk,
  CheckCircle,
  Percent,
  TextT,
  CircleNotch,
  Warning,
  Buildings,
  CreditCard,
  User,
  Hash,
  Briefcase,
  Shield,
  Prohibit,
  LockKey,
} from "@phosphor-icons/react";

export interface SettingsField {
  key: string;
  label: string;
  description: string;
  type: "number" | "text" | "password" | "toggle" | "select";
  icon: React.ComponentType<{ className?: string }>;
  options?: Array<{ value: string; label: string }>;
  step?: string;
  min?: string;
  max?: string;
  placeholder?: string;
}

export const SETTINGS_FIELDS: SettingsField[] = [
  {
    key: "platform_fee_percent",
    label: "Provizija platforme",
    description:
      "Procenat koji platforma uzima od svake transakcije terapeuta.",
    type: "number",
    icon: Percent,
    step: "0.1",
    min: "0",
    max: "100",
    placeholder: "npr. 20",
  },
  {
    key: "platform_name",
    label: "Naziv platforme",
    description: "Javni naziv koji se prikazuje korisnicima na platformi.",
    type: "text",
    icon: TextT,
    placeholder: "npr. Psihotron",
  },
  {
    key: PLATFORM_CLOSED_KEY,
    label: "Zatvori platformu",
    description:
      "Kada je aktivno, javne stranice prikazuju samo platform lock zid.",
    type: "toggle",
    icon: LockKey,
  },
  {
    key: PLATFORM_LOCK_PASSWORD_INPUT_KEY,
    label: "Platform lock šifra",
    description:
      "Odvojena šifra za otključavanje javnog dijela. Ne prikazuje se nakon spremanja.",
    type: "password",
    icon: LockKey,
    placeholder: "Unesite novu šifru za postavljanje ili promjenu",
  },
  {
    key: "bank_name",
    label: "Naziv banke",
    description: "Naziv banke za uplatu putem bankovnog transfera.",
    type: "text",
    icon: Buildings,
    placeholder: "npr. UniCredit Bank d.d.",
  },
  {
    key: "bank_account_number",
    label: "Broj računa",
    description: "IBAN ili broj računa za prijem uplata.",
    type: "text",
    icon: CreditCard,
    placeholder: "npr. BA391234567890123456",
  },
  {
    key: "bank_recipient_name",
    label: "Primalac",
    description: "Naziv primaoca na koji klijent uplaćuje.",
    type: "text",
    icon: User,
    placeholder: "npr. Psihotron d.o.o.",
  },
  {
    key: "bank_reference_prefix",
    label: "Prefiks reference",
    description: "Prefiks koji se dodaje ispred broja sesije u pozivu na broj.",
    type: "text",
    icon: Hash,
    placeholder: "npr. PSI-",
  },
  {
    key: "business_pricing_force_default",
    label: "Poslovni računi plaćaju cijene za BiH",
    description:
      "Kada je aktivno, svi korisnici povezani s firmom plaćaju cijene definirane za Bosnu i Hercegovinu (BA), bez obzira na detektovanu zemlju.",
    type: "toggle",
    icon: Briefcase,
  },
  {
    key: "cancel_deadline_hours",
    label: "Rok za otkazivanje (sati)",
    description:
      "Koliko sati prije sesije klijent može besplatno otkazati. Poslije ovog roka primjenjuje se naknada.",
    type: "number",
    icon: Shield,
    step: "1",
    min: "0",
    max: "168",
    placeholder: "npr. 24",
  },
  {
    key: "cancel_fee_percent",
    label: "Naknada za kasno otkazivanje (%)",
    description:
      "Procenat cijene usluge koji se naplaćuje klijentu ako otkaže nakon isteka roka za otkazivanje.",
    type: "number",
    icon: Prohibit,
    step: "1",
    min: "0",
    max: "100",
    placeholder: "npr. 50",
  },
  {
    key: "session_vat_rate",
    label: "PDV stopa za seanse",
    description:
      "Upisuje se kao decimalni broj: 0.17 znači 17%. Važi samo za nove rezervacije, već obračunate seanse zadržavaju stopu iz trenutka rezervacije.",
    type: "number",
    icon: Percent,
    step: "0.01",
    min: "0",
    max: "0.99",
    placeholder: "npr. 0.17",
  },
  {
    key: "wc_recognized_costs_percent",
    label: "Ugovor o djelu: priznati troškovi (%)",
    description: "Procenat bruto iznosa koji se priznaje kao trošak prije obračuna poreza.",
    type: "number",
    icon: Percent,
    step: "0.1",
    min: "0",
    max: "100",
    placeholder: "npr. 20",
  },
  {
    key: "wc_income_tax_percent",
    label: "Ugovor o djelu: porez na dohodak (%)",
    description: "Obračunava se na dohodak umanjen za doprinose (bruto minus priznati troškovi minus PIO i zdravstvo).",
    type: "number",
    icon: Percent,
    step: "0.1",
    min: "0",
    max: "100",
    placeholder: "npr. 10",
  },
  {
    key: "wc_pension_percent",
    label: "Ugovor o djelu: doprinos za PIO (%)",
    description: "Obračunava se na dohodak, tj. bruto minus priznati troškovi.",
    type: "number",
    icon: Percent,
    step: "0.1",
    min: "0",
    max: "100",
    placeholder: "npr. 6",
  },
  {
    key: "wc_health_percent",
    label: "Ugovor o djelu: doprinos za zdravstvo (%)",
    description: "Obračunava se na dohodak, tj. bruto minus priznati troškovi.",
    type: "number",
    icon: Percent,
    step: "0.1",
    min: "0",
    max: "100",
    placeholder: "npr. 4",
  },
];

/**
 * Only the fields this form owns, and only the ones that changed. Sending every
 * row from GET used to push `footer_config` and `email_tpl_*` blobs back through
 * this endpoint and fail on their length.
 */
export function buildSettingsPayload(
  formData: Record<string, string>,
  original: Record<string, string> = {},
): Record<string, string> {
  const payload: Record<string, string> = {};
  for (const field of SETTINGS_FIELDS) {
    const value = formData[field.key];
    if (value === undefined) continue;
    if (field.key === PLATFORM_LOCK_PASSWORD_INPUT_KEY) {
      if (value.trim()) payload[field.key] = value;
      continue;
    }
    if (value === (original[field.key] ?? "")) continue;
    payload[field.key] = field.type === "toggle" ? value : value.trim();
  }
  return payload;
}

export function PlatformSettingsSection() {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["admin", "settings"],
    queryFn: async () => {
      const res = await fetch("/api/settings");
      await throwIfNotOk(res, "Greška pri učitavanju postavki");
      return res.json() as Promise<Record<string, string>>;
    },
  });

  useEffect(() => {
    if (settings) {
      setFormData(settings);
      setHasChanges(false);
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: async (data: Record<string, string>) => {
      const invalid = validateSettingsPatch(data);
      if (invalid) throw new Error(invalid);
      const res = await fetch("/api/settings", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      await throwIfNotOk(res, "Greška pri spremanju postavki");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "settings"] });
      setSaved(true);
      setHasChanges(false);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const updateValue = (key: string, value: string) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSaved(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = buildSettingsPayload(formData, settings);
    if (Object.keys(payload).length === 0) {
      setHasChanges(false);
      return;
    }
    saveMutation.mutate(payload);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(2)].map((_, i) => (
          <div
            key={i}
            className="rounded-xl border bg-card p-5"
          >
            <div className="flex items-start gap-4">
              <div className="size-10 animate-pulse rounded-xl bg-muted" />
              <div className="flex-1 space-y-2">
                <div className="h-5 w-40 animate-pulse rounded bg-muted" />
                <div className="h-3.5 w-64 animate-pulse rounded bg-muted/60" />
                <div className="h-10 w-full animate-pulse rounded-lg bg-muted/40" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Known settings */}
      {SETTINGS_FIELDS.map((field) => {
        const Icon = field.icon;
        const isToggle = field.type === "toggle";
        const toggleOn = formData[field.key] === "true";
        return (
          <div
            key={field.key}
            className="rounded-xl border bg-card p-4 shadow-sm transition-shadow hover:shadow-md sm:p-5"
          >
            <div className="flex items-start gap-4">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-primary/8">
                <Icon className="size-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <Label
                      htmlFor={field.key}
                      className="text-base font-semibold text-foreground"
                      style={{ fontFamily: "var(--font-heading)" }}
                    >
                      {field.label}
                    </Label>
                    <p className="mt-0.5 text-sm text-muted-foreground">
                      {field.description}
                    </p>
                  </div>
                  {isToggle && (
                    <button
                      type="button"
                      id={field.key}
                      role="switch"
                      aria-checked={toggleOn}
                      onClick={() =>
                        updateValue(field.key, toggleOn ? "false" : "true")
                      }
                      className={cn(
                        "relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors",
                        toggleOn ? "bg-primary" : "bg-muted",
                      )}
                    >
                      <span
                        className={cn(
                          "inline-block size-5 transform rounded-full bg-white shadow transition-transform",
                          toggleOn ? "translate-x-5" : "translate-x-0.5",
                        )}
                      />
                    </button>
                  )}
                </div>
                {field.type === "select" && (
                  <div className="mt-3">
                    <select
                      id={field.key}
                      value={formData[field.key] || field.options?.[0]?.value || ""}
                      onChange={(e) => updateValue(field.key, e.target.value)}
                      className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                    >
                      {field.options?.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {!isToggle && field.type !== "select" && (
                  <div className="mt-3">
                    <Input
                      id={field.key}
                      type={field.type}
                      step={field.step}
                      min={field.min}
                      max={field.max}
                      placeholder={field.placeholder}
                      value={formData[field.key] ?? ""}
                      onChange={(e) => updateValue(field.key, e.target.value)}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Save bar */}
      <div
        className={cn(
          "sticky bottom-4 flex items-center justify-between rounded-xl border bg-card/95 p-4 shadow-lg backdrop-blur-sm transition-all sm:p-5",
          hasChanges
            ? "ring-2 ring-primary/20"
            : "ring-1 ring-border/50"
        )}
      >
        <div className="flex items-center gap-2 text-sm">
          {saved ? (
            <>
              <CheckCircle className="size-4 text-emerald-600" />
              <span className="font-medium text-emerald-600">
                Postavke spremljene
              </span>
            </>
          ) : hasChanges ? (
            <>
              <Warning className="size-4 text-amber-500" />
              <span className="text-muted-foreground">
                Imate nesačuvane promjene
              </span>
            </>
          ) : (
            <span className="text-muted-foreground">
              Sve postavke su ažurne
            </span>
          )}
        </div>
        <Button
          type="submit"
          disabled={saveMutation.isPending || !hasChanges}
        >
          {saveMutation.isPending ? (
            <CircleNotch className="size-4 animate-spin" />
          ) : (
            <FloppyDisk className="size-4" />
          )}
          {saveMutation.isPending ? "Spremanje..." : "Spremi postavke"}
        </Button>
      </div>
    </form>
  );
}
